import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getConfig } from '../config';
import {
    deleteSpecialCustomerRequest,
    getSpecialCustomersRequest,
    importSpecialCustomersRequest,
    loginRequest,
    saveSpecialCustomerRequest,
} from '../services/validationApi';

const emptyForm = {
    customerCode: '',
    resal: '',
    statusType: 'M',
    enteredBy: '',
};

const getErrorMessage = (error, fallback) =>
    error.response?.data?.message || error.message || fallback;

const dataTableColumns = [
    { key: 'company', label: 'Compania' },
    { key: 'customerCode', label: 'Codigo' },
    { key: 'resal', label: 'Documento / resal' },
    { key: 'statusType', label: 'Tipo' },
    { key: 'enteredBy', label: 'Usuario' },
    { key: 'entryDate', label: 'Fecha' },
];

const getSortableValue = (customer, key) => {
    if (key === 'entryDate') {
        return customer.entryDate ? new Date(customer.entryDate).getTime() : 0;
    }

    return (customer[key] || '').toString().toLowerCase();
};

const normalizeCsvHeader = (value) =>
    value
        .replace(/^\uFEFF/, '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\s_-]+/g, '');

const parseCsvLine = (line, delimiter) => {
    const values = [];
    let currentValue = '';
    let isQuoted = false;

    for (let index = 0; index < line.length; index++) {
        const character = line[index];
        const nextCharacter = line[index + 1];

        if (character === '"' && isQuoted && nextCharacter === '"') {
            currentValue += '"';
            index++;
            continue;
        }

        if (character === '"') {
            isQuoted = !isQuoted;
            continue;
        }

        if (character === delimiter && !isQuoted) {
            values.push(currentValue.trim());
            currentValue = '';
            continue;
        }

        currentValue += character;
    }

    values.push(currentValue.trim());
    return values;
};

const resolveHeaderIndex = (headers, aliases) =>
    headers.findIndex((header) => aliases.includes(header));

const parseImportCsv = (text) => {
    const lines = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length < 2) {
        throw new Error('El archivo debe tener encabezados y al menos un cliente.');
    }

    const delimiter = (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length
        ? ';'
        : ',';
    const headers = parseCsvLine(lines[0], delimiter).map(normalizeCsvHeader);
    const resalIndex = resolveHeaderIndex(headers, [
        'documento',
        'resal',
        'documentoresal',
        'documentooresal',
        'cweresal',
    ]);
    const customerCodeIndex = resolveHeaderIndex(headers, [
        'codigo',
        'codigocliente',
        'codigointerno',
        'codigointernodelcliente',
        'customercode',
        'cwecust',
    ]);
    const statusTypeIndex = resolveHeaderIndex(headers, [
        'tipo',
        'statustype',
        'cwestatustype',
    ]);
    const enteredByIndex = resolveHeaderIndex(headers, [
        'usuario',
        'usuarioregistra',
        'usuarioqueregistra',
        'enteredby',
        'cweenter',
    ]);

    if (resalIndex < 0) {
        throw new Error('El archivo debe tener una columna documento o resal.');
    }

    const rows = lines.slice(1).map((line, index) => {
        const columns = parseCsvLine(line, delimiter);
        return {
            rowNumber: index + 2,
            company: 'DD',
            resal: columns[resalIndex]?.trim() || '',
            customerCode: customerCodeIndex >= 0 ? columns[customerCodeIndex]?.trim() || '' : '',
            statusType: statusTypeIndex >= 0
                ? columns[statusTypeIndex]?.trim().toUpperCase() || 'M'
                : 'M',
            enteredBy: enteredByIndex >= 0 ? columns[enteredByIndex]?.trim() || '' : '',
        };
    }).filter((row) => row.resal || row.customerCode);

    if (rows.length === 0) {
        throw new Error('No encontre clientes validos para importar.');
    }

    if (rows.length > 500) {
        throw new Error('Solo puedes importar hasta 500 clientes por archivo.');
    }

    return rows;
};

const SpecialCustomersPage = () => {
    const { apiBaseURL } = getConfig();
    const [auth, setAuth] = useState(() => {
        const token = sessionStorage.getItem('specialCustomersToken');
        const username = sessionStorage.getItem('specialCustomersUsername');
        return token ? { token, username } : null;
    });
    const [loginForm, setLoginForm] = useState({
        username: '',
        password: '',
    });
    const [customers, setCustomers] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [search, setSearch] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [importRows, setImportRows] = useState([]);
    const [importFileName, setImportFileName] = useState('');
    const [importSummary, setImportSummary] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: 'entryDate',
        direction: 'desc',
    });
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchCustomers = useCallback(async (nextSearch = search) => {
        if (!auth?.token) {
            return;
        }

        try {
            setIsLoading(true);
            setError('');
            const response = await getSpecialCustomersRequest(
                apiBaseURL,
                nextSearch.trim(),
                auth.token
            );
            setCustomers(response.data?.data || []);
            setCurrentPage(1);
        } catch (requestError) {
            setError(getErrorMessage(requestError, 'No se pudo consultar clientes especiales.'));
            setCustomers([]);
        } finally {
            setIsLoading(false);
        }
    }, [apiBaseURL, auth?.token, search]);

    useEffect(() => {
        if (auth?.token) {
            fetchCustomers('');
        }
    }, [auth?.token, fetchCustomers]);

    useEffect(() => {
        setCurrentPage(1);
    }, [customers.length, pageSize]);

    const sortedCustomers = useMemo(() => {
        const nextCustomers = [...customers];

        nextCustomers.sort((leftCustomer, rightCustomer) => {
            const leftValue = getSortableValue(leftCustomer, sortConfig.key);
            const rightValue = getSortableValue(rightCustomer, sortConfig.key);

            if (leftValue < rightValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }

            if (leftValue > rightValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }

            return 0;
        });

        return nextCustomers;
    }, [customers, sortConfig]);

    const totalRecords = sortedCustomers.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startRecord = totalRecords === 0 ? 0 : ((safeCurrentPage - 1) * pageSize) + 1;
    const endRecord = Math.min(safeCurrentPage * pageSize, totalRecords);
    const paginatedCustomers = sortedCustomers.slice(startRecord - 1, endRecord);

    const handleSort = (key) => {
        setSortConfig((currentSort) => ({
            key,
            direction: currentSort.key === key && currentSort.direction === 'asc'
                ? 'desc'
                : 'asc',
        }));
    };

    const handleLoginChange = (event) => {
        const { name, value } = event.target;
        setLoginForm((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
    };

    const handleLogin = async (event) => {
        event.preventDefault();

        if (!loginForm.username.trim() || !loginForm.password) {
            setError('Ingresa usuario y contrasena de red.');
            return;
        }

        try {
            setIsLoggingIn(true);
            setError('');
            setMessage('');

            const response = await loginRequest(apiBaseURL, {
                username: loginForm.username.trim(),
                password: loginForm.password,
            });

            const nextAuth = {
                token: response.data?.token,
                username: response.data?.username || loginForm.username.trim(),
            };

            sessionStorage.setItem('specialCustomersToken', nextAuth.token);
            sessionStorage.setItem('specialCustomersUsername', nextAuth.username);
            setAuth(nextAuth);
            setLoginForm({ username: '', password: '' });
        } catch (requestError) {
            setAuth(null);
            sessionStorage.removeItem('specialCustomersToken');
            sessionStorage.removeItem('specialCustomersUsername');
            setError(getErrorMessage(requestError, 'No se pudo iniciar sesion contra Active Directory.'));
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleLogout = () => {
        sessionStorage.removeItem('specialCustomersToken');
        sessionStorage.removeItem('specialCustomersUsername');
        setAuth(null);
        setCustomers([]);
        setMessage('');
        setError('');
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.resal.trim()) {
            setError('Ingresa el documento o resal del cliente.');
            return;
        }

        try {
            setIsSaving(true);
            setError('');
            setMessage('');

            const response = await saveSpecialCustomerRequest(apiBaseURL, {
                company: 'DD',
                customerCode: form.customerCode.trim(),
                resal: form.resal.trim(),
                statusType: form.statusType,
                enteredBy: form.enteredBy.trim() || auth?.username,
            }, auth?.token);

            setMessage(response.data?.message || 'Cliente especial guardado correctamente.');
            setForm(emptyForm);
            await fetchCustomers(search);
        } catch (requestError) {
            setError(getErrorMessage(requestError, 'No se pudo guardar el cliente especial.'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleSearch = async (event) => {
        event.preventDefault();
        setMessage('');
        await fetchCustomers(search);
    };

    const handleClearSearch = async () => {
        setSearch('');
        setMessage('');
        await fetchCustomers('');
    };

    const handleDelete = async (customer) => {
        const confirmed = window.confirm(
            `Vas a eliminar el cliente especial ${customer.resal || customer.customerCode}.`
        );

        if (!confirmed) {
            return;
        }

        try {
            setError('');
            setMessage('');
            await deleteSpecialCustomerRequest(apiBaseURL, {
                customerCode: customer.customerCode,
                resal: customer.resal,
            }, auth?.token);
            setMessage('Cliente especial eliminado correctamente.');
            await fetchCustomers(search);
        } catch (requestError) {
            setError(getErrorMessage(requestError, 'No se pudo eliminar el cliente especial.'));
        }
    };

    const handleEdit = (customer) => {
        setForm({
            customerCode: customer.customerCode || '',
            resal: customer.resal || '',
            statusType: customer.statusType || 'M',
            enteredBy: customer.enteredBy || '',
        });
        setMessage('Registro cargado para actualizar.');
        setError('');
    };

    const handleDownloadTemplate = () => {
        const template = [
            'documento,codigo,tipo,usuario_registra',
            '909355,,M,',
        ].join('\n');
        const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'plantilla-clientes-especiales.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleImportFileChange = async (event) => {
        const file = event.target.files?.[0];

        setImportRows([]);
        setImportFileName('');
        setImportSummary(null);
        setMessage('');

        if (!file) {
            return;
        }

        try {
            const text = await file.text();
            const rows = parseImportCsv(text);
            setImportRows(rows);
            setImportFileName(file.name);
            setError('');
            setMessage(`${rows.length} cliente(s) listos para importar.`);
        } catch (parseError) {
            setError(parseError.message || 'No se pudo leer el archivo.');
        } finally {
            event.target.value = '';
        }
    };

    const handleImport = async () => {
        if (importRows.length === 0) {
            setError('Selecciona un archivo con clientes para importar.');
            return;
        }

        try {
            setIsImporting(true);
            setError('');
            setMessage('');
            setImportSummary(null);

            const response = await importSpecialCustomersRequest(
                apiBaseURL,
                importRows.map(({ rowNumber, ...row }) => ({
                    ...row,
                    enteredBy: row.enteredBy || auth?.username,
                })),
                auth?.token
            );

            setImportSummary(response.data);
            setMessage(response.data?.message || 'Importacion finalizada.');
            setImportRows([]);
            setImportFileName('');
            await fetchCustomers(search);
        } catch (requestError) {
            setError(getErrorMessage(requestError, 'No se pudo importar el archivo.'));
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <main className="min-h-screen bg-slate-100 text-slate-900">
            <section className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8">
                <div className="flex flex-col gap-3 border-b border-slate-200 pb-5 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                            Portal administrativo
                        </p>
                        <h1 className="mt-1 text-3xl font-bold text-slate-950">
                            Clientes especiales
                        </h1>
                    </div>
                    <a
                        href="/"
                        className="inline-flex h-10 items-center justify-center rounded-lg border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-white"
                    >
                        Volver al portal
                    </a>
                </div>

                {auth && (
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                        <span>Sesion iniciada</span>
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50"
                        >
                            Cerrar sesion
                        </button>
                    </div>
                )}

                {(message || error) && (
                    <div
                        className={`rounded-lg border px-4 py-3 text-sm font-medium ${
                            error
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-green-200 bg-green-50 text-green-700'
                        }`}
                    >
                        {error || message}
                    </div>
                )}

                {!auth && (
                    <form
                        onSubmit={handleLogin}
                        className="mx-auto w-full max-w-md rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
                    >
                        <h2 className="text-lg font-bold text-slate-950">Acceso administrativo</h2>

                        <label className="mt-5 block text-sm font-semibold text-slate-700">
                            Usuario
                            <input
                                name="username"
                                value={loginForm.username}
                                onChange={handleLoginChange}
                                className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-700"
                                placeholder="Usuario de red"
                                autoComplete="username"
                            />
                        </label>

                        <label className="mt-4 block text-sm font-semibold text-slate-700">
                            Contrasena
                            <span className="relative mt-2 block">
                                <input
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={loginForm.password}
                                    onChange={handleLoginChange}
                                    className="h-11 w-full rounded-lg border border-slate-300 px-3 pr-11 text-sm outline-none focus:border-slate-700"
                                    placeholder="Contrasena de red"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((currentValue) => !currentValue)}
                                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-500 transition hover:text-slate-900"
                                    aria-label={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                                    title={showPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                                >
                                    {showPassword ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="h-5 w-5"
                                        >
                                            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-8a12.35 12.35 0 0 1 3.12-4.77" />
                                            <path d="M9.9 4.24A10.89 10.89 0 0 1 12 4c5 0 9.27 3.11 11 8a12.36 12.36 0 0 1-2.2 3.37" />
                                            <path d="M14.12 14.12A3 3 0 0 1 9.88 9.88" />
                                            <path d="M1 1l22 22" />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="h-5 w-5"
                                        >
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </span>
                        </label>

                        <button
                            type="submit"
                            disabled={isLoggingIn}
                            className="mt-6 h-11 w-full rounded-lg bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                        >
                            {isLoggingIn ? 'Validando...' : 'Ingresar'}
                        </button>
                    </form>
                )}

                {auth && (
                <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
                    <div className="flex flex-col gap-6">
                        <form
                            onSubmit={handleSubmit}
                            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
                        >
                            <h2 className="text-lg font-bold text-slate-950">Guardar cliente</h2>

                            <label className="mt-5 block text-sm font-semibold text-slate-700">
                                Documento o resal
                                <input
                                    name="resal"
                                    value={form.resal}
                                    onChange={handleChange}
                                    className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-700"
                                    placeholder="Ej: 909355"
                                />
                            </label>

                            <label className="mt-4 block text-sm font-semibold text-slate-700">
                                Codigo interno del cliente
                                <input
                                    name="customerCode"
                                    value={form.customerCode}
                                    onChange={handleChange}
                                    className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-700"
                                    placeholder="Opcional"
                                />
                            </label>

                            <label className="mt-4 block text-sm font-semibold text-slate-700">
                                Tipo
                                <select
                                    name="statusType"
                                    value={form.statusType}
                                    onChange={handleChange}
                                    className="mt-2 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-700"
                                >
                                    <option value="M">M - Cliente especial</option>
                                    <option value="A">A - Cliente automatico</option>
                                </select>
                            </label>

                            <label className="mt-4 block text-sm font-semibold text-slate-700">
                                Usuario que registra
                                <input
                                    name="enteredBy"
                                    value={form.enteredBy}
                                    onChange={handleChange}
                                    className="mt-2 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-700"
                                    placeholder="Opcional"
                                />
                            </label>

                            <button
                                type="submit"
                                disabled={isSaving}
                                className="mt-6 h-11 w-full rounded-lg bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                            >
                                {isSaving ? 'Guardando...' : 'Guardar'}
                            </button>
                        </form>

                        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                            <h2 className="text-lg font-bold text-slate-950">Importar clientes</h2>
                            <p className="mt-2 text-sm text-slate-600">
                                Carga un archivo CSV. La columna documento es obligatoria.
                            </p>

                            <div className="mt-4 flex flex-col gap-3">
                                <button
                                    type="button"
                                    onClick={handleDownloadTemplate}
                                    className="h-10 rounded-lg border border-slate-300 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                                >
                                    Descargar plantilla
                                </button>

                                <label className="flex cursor-pointer items-center justify-center rounded-lg border border-dashed border-slate-300 px-4 py-4 text-center text-sm font-semibold text-slate-600 transition hover:border-slate-500 hover:bg-slate-50">
                                    Seleccionar archivo CSV
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={handleImportFileChange}
                                        className="hidden"
                                    />
                                </label>

                                {importFileName && (
                                    <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
                                        Archivo: {importFileName}
                                    </p>
                                )}

                                {importRows.length > 0 && (
                                    <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700">
                                        {importRows.length} cliente(s) listos para cargar.
                                    </p>
                                )}

                                <button
                                    type="button"
                                    onClick={handleImport}
                                    disabled={isImporting || importRows.length === 0}
                                    className="h-11 rounded-lg bg-slate-950 px-4 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                                >
                                    {isImporting ? 'Importando...' : 'Importar'}
                                </button>

                                {importSummary && (
                                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-700">
                                        <p className="font-bold">
                                            Total: {importSummary.total} | Creados: {importSummary.created} | Actualizados: {importSummary.updated} | Errores: {importSummary.failed}
                                        </p>
                                        {importSummary.failed > 0 && (
                                            <ul className="mt-2 max-h-32 space-y-1 overflow-auto">
                                                {importSummary.results
                                                    ?.filter((item) => item.status === 'Error')
                                                    .slice(0, 10)
                                                    .map((item) => (
                                                        <li key={`${item.rowNumber}-${item.resal}`}>
                                                            Fila {item.rowNumber}: {item.message}
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                        <form onSubmit={handleSearch} className="flex flex-col gap-3 md:flex-row">
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                className="h-11 flex-1 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-slate-700"
                                placeholder="Buscar por documento, resal o codigo"
                            />
                            <button
                                type="submit"
                                className="h-11 rounded-lg bg-slate-950 px-5 text-sm font-bold text-white transition hover:bg-slate-800"
                            >
                                Buscar
                            </button>
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="h-11 rounded-lg border border-slate-300 px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                            >
                                Limpiar
                            </button>
                        </form>

                        <div className="mt-5 flex flex-col gap-3 border-y border-slate-100 py-3 md:flex-row md:items-center md:justify-between">
                            <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                                Mostrar
                                <select
                                    value={pageSize}
                                    onChange={(event) => setPageSize(Number(event.target.value))}
                                    className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm outline-none focus:border-slate-700"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                registros
                            </label>

                            <p className="text-sm font-semibold text-slate-500">
                                {isLoading
                                    ? 'Consultando...'
                                    : `Mostrando ${startRecord} a ${endRecord} de ${totalRecords} registro(s)`}
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                                        {dataTableColumns.map((column) => (
                                            <th key={column.key} className="px-3 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSort(column.key)}
                                                    className="flex items-center gap-1 font-bold uppercase text-slate-500 transition hover:text-slate-900"
                                                >
                                                    {column.label}
                                                    <span className="text-[10px]">
                                                        {sortConfig.key === column.key
                                                            ? (sortConfig.direction === 'asc' ? '▲' : '▼')
                                                            : '↕'}
                                                    </span>
                                                </button>
                                            </th>
                                        ))}
                                        <th className="px-3 py-3 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedCustomers.map((customer) => (
                                        <tr
                                            key={`${customer.company}-${customer.customerCode}-${customer.resal}`}
                                            className="border-b border-slate-100"
                                        >
                                            <td className="px-3 py-3">{customer.company}</td>
                                            <td className="px-3 py-3 font-semibold">
                                                {customer.customerCode}
                                            </td>
                                            <td className="px-3 py-3">{customer.resal}</td>
                                            <td className="px-3 py-3">
                                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">
                                                    {customer.statusType}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3">{customer.enteredBy}</td>
                                            <td className="px-3 py-3">
                                                {customer.entryDate
                                                    ? new Date(customer.entryDate).toLocaleDateString()
                                                    : ''}
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEdit(customer)}
                                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50"
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(customer)}
                                                        className="rounded-lg border border-red-200 px-3 py-2 text-xs font-bold text-red-700 transition hover:bg-red-50"
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}

                                    {!isLoading && totalRecords === 0 && (
                                        <tr>
                                            <td
                                                colSpan="7"
                                                className="px-3 py-10 text-center text-sm text-slate-500"
                                            >
                                                No hay clientes especiales para mostrar.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {isLoading && (
                            <p className="mt-4 text-center text-sm font-semibold text-slate-500">
                                Consultando...
                            </p>
                        )}

                        {!isLoading && totalRecords > 0 && (
                            <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 md:flex-row md:items-center md:justify-between">
                                <p className="text-sm font-semibold text-slate-500">
                                    Pagina {safeCurrentPage} de {totalPages}
                                </p>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Primero
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={safeCurrentPage === totalPages}
                                        className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Ultimo
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                )}
            </section>
        </main>
    );
};

export default SpecialCustomersPage;
